"use client";
import SectionWrapper, { basePx } from "@/commonComponents/section-wrapper";
import {
  MotionImage,
  MotionStack,
  MotionTypography,
} from "@/commonComponents/motion-components";
import { H1, H6_2 } from "@/styles/theme/components/typography.fontvariant";
import ExpandableAccordion, {
  ExpandableAccordionProps,
} from "@/commonComponents/expandable-accordion";
import { Stack } from "@mui/material";
import { FixedLengthArray } from "type-fest";
import { StaticImageData } from "next/image";
import ArrowGradient from "@cc/arrow-gradient";
import AnimatedButton from "@cc/animated-button";
import {
  arrowLeftToRightTransition,
  bottomToToptextTransition,
} from "@cc/animations";
import { isNotEmptyObject } from "class-validator";

export type OurFocusAreasProps = {
  title?: string;
  featuredData?: {
    description: string;
    images: FixedLengthArray<StaticImageData | string, 3>;
    btnText?: string;
    btnLink?: string;
  };
  accordionData?: ExpandableAccordionProps["data"];
  navId?: string;
};

const OurFocusAreas = (props: OurFocusAreasProps) => {
  const { title, featuredData = {}, accordionData, navId } = props;
  const { description, images, btnText, btnLink } =
    featuredData as Required<OurFocusAreasProps>["featuredData"];
  const [img1, img2, img3] = images;
  return (
    <SectionWrapper
      color={"secondary.main"}
      navId={navId || "programs-&-initiatives"}
      SectionProps={{
        sx: {
          background: (theme) => theme.palette.gradient.darkToLight,
        },
      }}
      px={{ ...basePx, xs: 0 }}
    >
      {featuredData && isNotEmptyObject(featuredData) && (
        <>
          {title && (
            <MotionStack
              direction={"row"}
              justifyContent={{ xs: "center", md: "flex-start" }}
              columnGap={6.5}
              alignItems={"center"}
              mb={{ xs: 4, md: 3 }}
              px={{ ...basePx, md: 0, xxl: 0 }}
            >
              <ArrowGradient
                SVGProps={{
                  width: 60,
                  height: 156,
                }}
                sx={{
                  display: { xs: "none", md: "block" },
                }}
                {...arrowLeftToRightTransition}
              />
              <MotionTypography
                fontSize={H1}
                variants={bottomToToptextTransition()}
                initial="initial"
                whileInView={"animate"}
                viewport={{ once: true }}
              >
                {title}
              </MotionTypography>
            </MotionStack>
          )}
          {images.length >= 3 && (
            <Stack
              direction={{ xs: "column", lg: "row" }}
              alignItems={"center"}
              justifyContent={"space-between"}
              spacing={{ xs: 4, lg: 8 }}
              mb={{ xs: 6, md: 8 }}
              overflow={"hidden"}
            >
              <Stack
                direction={"row"}
                spacing={{ xs: 0.5, md: 1 }}
                width={{ xs: 1, lg: 0.5 }}
              >
                <MotionImage
                  src={img1}
                  alt="img1"
                  width={318}
                  height={368}
                  sx={{
                    width: 0.5,
                    maxWidth: { xs: "none", lg: 318 },
                    height: { lg: 368 },
                    objectFit: "cover",
                  }}
                />
                <Stack
                  width={0.5}
                  rowGap={{ xs: 0.5, md: 1 }}
                  maxWidth={{ xs: "none", lg: 318 }}
                  height={"auto"}
                >
                  <MotionImage
                    src={img2}
                    alt={"img2"}
                    width={318}
                    height={184}
                    sx={{
                      width: 1,
                      height: { xs: 0.5, lg: 0.5 },
                      objectFit: "cover",
                    }}
                  />
                  <MotionImage
                    src={img3}
                    alt={"img3"}
                    width={318}
                    height={184}
                    sx={{
                      width: 1,
                      height: { xs: 0.5, lg: 0.5 },
                      objectFit: "cover",
                    }}
                  />
                </Stack>
              </Stack>
              {(description || (btnText && btnLink)) && (
                <Stack
                  width={{ xs: 1, lg: 0.5 }}
                  rowGap={{ xs: 3, md: 5 }}
                  px={{ ...basePx, md: 0, xxl: 0 }}
                >
                  {description && (
                    <MotionTypography
                      variant="body1"
                      fontSize={H6_2}
                      maxWidth={741}
                      textAlign={"justify"}
                    >
                      {description}
                    </MotionTypography>
                  )}
                  {btnText && btnLink && (
                    <AnimatedButton
                      href={btnLink}
                      color={"secondary"}
                      variant={"contained"}
                      sx={{
                        width: { xs: 1, md: "fit-content" },
                      }}
                    >
                      {btnText}
                    </AnimatedButton>
                  )}
                </Stack>
              )}
            </Stack>
          )}
        </>
      )}
      {accordionData &&
        accordionData.length > 0 &&
        accordionData.some((item) => item.isActive) && (
          <Stack px={{ ...basePx, md: 0, xxl: 0 }}>
            <ExpandableAccordion
              data={accordionData}
              TopDivider
              BottomDivider
            />
          </Stack>
        )}
    </SectionWrapper>
  );
};

export default OurFocusAreas;
